export const OPERATORS_BY_TYPE = {
  string: [
    { op: 'in', label: 'is any of' },
    { op: 'not_in', label: 'is none of' },
    { op: 'eq', label: 'equals' },
    { op: 'neq', label: 'not equals' },
    { op: 'contains', label: 'contains' },
    { op: 'starts_with', label: 'starts with' },
    { op: 'ends_with', label: 'ends with' },
    { op: 'gte', label: '≥' },
    { op: 'lte', label: '≤' },
    { op: 'is_null', label: 'is empty' },
    { op: 'is_not_null', label: 'is not empty' },
  ],
  number: [
    { op: 'eq', label: '=' },
    { op: 'neq', label: '≠' },
    { op: 'gt', label: '>' },
    { op: 'gte', label: '≥' },
    { op: 'lt', label: '<' },
    { op: 'lte', label: '≤' },
    { op: 'between', label: 'between' },
    { op: 'is_null', label: 'is empty' },
    { op: 'is_not_null', label: 'is not empty' },
  ],
  date: [
    { op: 'eq', label: 'on' },
    { op: 'gte', label: 'on or after' },
    { op: 'lte', label: 'on or before' },
    { op: 'between', label: 'between' },
    { op: 'is_null', label: 'is empty' },
    { op: 'is_not_null', label: 'is not empty' },
  ],
  boolean: [
    { op: 'is_true', label: 'is yes' },
    { op: 'is_false', label: 'is no' },
  ],
};

const VALUELESS_OPS = new Set(['is_null', 'is_not_null', 'is_true', 'is_false']);
const MULTI_OPS = new Set(['in', 'not_in']);
const RANGE_OPS = new Set(['between']);

export function operatorsForColumn(column) {
  const type = column?.uiType || 'string';
  return OPERATORS_BY_TYPE[type] || OPERATORS_BY_TYPE.string;
}

export function defaultOpForColumn(column) {
  if (!column) return 'eq';
  if (column.uiType === 'boolean') return 'is_true';
  if (column.hasLookup) return 'in';
  if (column.name === 'Hole_ID') return 'starts_with';
  if (isFreeTextColumn(column.name)) return 'contains';
  return 'eq';
}

export function opNeedsValue(op) {
  return !VALUELESS_OPS.has(op);
}

export function opIsMulti(op) {
  return MULTI_OPS.has(op);
}

export function opIsRange(op) {
  return RANGE_OPS.has(op);
}

export function isFreeTextColumn(name) {
  return /comment|remark|purpose/i.test(String(name || ''));
}

export function newRuleId() {
  return `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function emptyRule(column) {
  return {
    id: newRuleId(),
    field: column?.name || '',
    op: defaultOpForColumn(column),
    value: column?.hasLookup ? [] : '',
    valueTo: '',
  };
}

export function emptyGroup() {
  return {
    id: newRuleId(),
    combinator: 'OR',
    rules: [emptyRule()],
  };
}

export function isGroup(node) {
  return !!(node && Array.isArray(node.rules));
}

function ruleHasValue(rule) {
  if (!rule?.field || !rule?.op) return false;
  if (!opNeedsValue(rule.op)) return true;
  if (opIsMulti(rule.op)) return Array.isArray(rule.value) && rule.value.length > 0;
  if (opIsRange(rule.op)) {
    const from = Array.isArray(rule.value) ? rule.value[0] : rule.value;
    const to = rule.valueTo ?? (Array.isArray(rule.value) ? rule.value[1] : '');
    return from !== '' && from != null && to !== '' && to != null;
  }
  return rule.value !== '' && rule.value != null;
}

export function sanitizeFilters(combinator, rules) {
  const cleaned = (rules || [])
    .map((node) => {
      if (isGroup(node) && node.field == null) {
        const inner = sanitizeFilters(node.combinator || 'OR', node.rules);
        return inner.rules.length ? inner : null;
      }
      if (!ruleHasValue(node)) return null;
      const next = { field: node.field, op: node.op };
      if (opIsRange(node.op)) {
        const from = Array.isArray(node.value) ? node.value[0] : node.value;
        next.value = from;
        next.valueTo = node.valueTo ?? (Array.isArray(node.value) ? node.value[1] : '');
      } else if (opNeedsValue(node.op)) {
        next.value = node.value;
      }
      return next;
    })
    .filter(Boolean);

  return {
    combinator: combinator === 'OR' ? 'OR' : 'AND',
    rules: cleaned,
  };
}

export function queryHasConstraint(dataset, filters) {
  if (dataset) return true;
  return Array.isArray(filters?.rules) && filters.rules.length > 0;
}

function formatRuleValue(rule) {
  if (!opNeedsValue(rule.op)) return '';
  if (Array.isArray(rule.value)) {
    const shown = rule.value.slice(0, 3).map(String);
    const extra = rule.value.length - shown.length;
    return extra > 0 ? `${shown.join(', ')} +${extra}` : shown.join(', ');
  }
  if (opIsRange(rule.op)) {
    return `${rule.value ?? ''}–${rule.valueTo ?? ''}`;
  }
  return String(rule.value ?? '');
}

function summarizeNode(node) {
  if (isGroup(node) && node.field == null) {
    const inner = (node.rules || []).map(summarizeNode).filter(Boolean);
    if (!inner.length) return '';
    return `(${inner.join(` ${node.combinator === 'OR' ? 'OR' : 'AND'} `)})`;
  }
  if (!node?.field) return '';
  const op = operatorsForColumn({ uiType: 'string' }).concat(
    OPERATORS_BY_TYPE.number,
    OPERATORS_BY_TYPE.date,
    OPERATORS_BY_TYPE.boolean
  ).find((o) => o.op === node.op);
  const opLabel = op?.label || node.op;
  const value = formatRuleValue(node);
  return value ? `${node.field} ${opLabel} ${value}` : `${node.field} ${opLabel}`;
}

export function summarizeQuery(dataset, filters, intervalMode, isPlan = false) {
  const parts = [];
  if (dataset) parts.push(dataset);
  const body = (filters?.rules || []).map(summarizeNode).filter(Boolean);
  if (body.length) {
    parts.push(body.join(` ${filters?.combinator === 'OR' ? 'OR' : 'AND'} `));
  }
  parts.push(intervalMode === 'lith1_code' ? 'lithology' : 'assay');
  parts.push(isPlan ? 'plan' : 'actual');
  return parts.join(' · ') || 'Custom holes';
}

export function mergeHoleQueryLayers(modelList, loadedLayers, sharedFilters) {
  const loaded = Array.isArray(loadedLayers) ? loadedLayers : [];
  const shared = (sharedFilters || []).map((filter) => sharedFilterToLayer(filter, loaded));
  const sharedNames = new Set(shared.map((layer) => layer.name));
  const customLoaded = loaded.filter((layer) => !sharedNames.has(layer.name));

  let next = (modelList || []).map((s) => ({ ...s, models: [...(s.models || [])] }));
  next = upsertHoleSection(next, 'Loaded queries', customLoaded);
  next = upsertHoleSection(next, 'Shared queries', shared);
  return next;
}

export function sharedFilterToLayer(filter, loadedLayers = []) {
  const clean = sanitizeFilters(filter?.combinator, filter?.rules);
  const name = `sharedQuery_${filter.id}`;
  const loaded = (loadedLayers || []).find((layer) => layer.name === name || layer.filterId === filter.id);
  const intervalMode = loaded?.interval_mode || (filter?.intervalMode === 'lith1_code' ? 'lith1_code' : 'assay');
  return {
    type: 'databaseHoles',
    name,
    displayName: filter?.name || 'Shared holes',
    filterName: filter?.name || 'Shared holes',
    filterId: filter?.id,
    dataset: filter?.dataset || loaded?.dataset || undefined,
    interval_mode: intervalMode,
    isplan: !!(filter?.isPlan || loaded?.isplan),
    collarQuery: loaded?.collarQuery || clean,
    queryLayer: true,
    sharedQuery: true,
    queryAutoLoad: !!loaded?.queryAutoLoad,
    max_rows: loaded?.max_rows || 20000,
  };
}

function upsertHoleSection(modelList, name, models) {
  const next = Array.isArray(modelList) ? [...modelList] : [];
  const idx = next.findIndex((s) => s.group === 'Holes' && s.name === name);
  if (!models.length) {
    if (idx >= 0) next.splice(idx, 1);
    return next;
  }
  const section = { group: 'Holes', name, models };
  if (idx >= 0) {
    const existing = next[idx].models.filter((m) => !m.queryLayer && !m.sharedQuery);
    next[idx] = { ...next[idx], models: [...models, ...existing] };
    return next;
  }
  const holesIdx = next.findIndex((s) => s.group === 'Holes');
  if (name === 'Shared queries') {
    const loadedIdx = next.findIndex((s) => s.group === 'Holes' && s.name === 'Loaded queries');
    if (loadedIdx >= 0) {
      next.splice(loadedIdx + 1, 0, section);
      return next;
    }
  }
  if (holesIdx >= 0) {
    next.splice(holesIdx, 0, section);
    return next;
  }
  return [section, ...next];
}

export function cloneFilterRules(rules) {
  return (rules || []).map((node) => {
    if (isGroup(node) && node.field == null) {
      return {
        id: newRuleId(),
        combinator: node.combinator === 'OR' ? 'OR' : 'AND',
        rules: cloneFilterRules(node.rules),
      };
    }
    return {
      id: newRuleId(),
      field: node.field || '',
      op: node.op || 'eq',
      value: Array.isArray(node.value) ? [...node.value] : node.value ?? '',
      valueTo: node.valueTo ?? '',
    };
  });
}

export function draftFromSavedFilter(filter) {
  const rules = cloneFilterRules(filter?.rules);
  return {
    dataset: filter?.dataset || '',
    intervalMode: filter?.intervalMode === 'lith1_code' ? 'lith1_code' : 'assay',
    combinator: filter?.combinator === 'OR' ? 'OR' : 'AND',
    isPlan: !!filter?.isPlan,
    rules: rules.length ? rules : [emptyRule()],
  };
}

export function newSavedFilterId() {
  return `saved_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function filterRecordFromDraft(draft, { id, name, shared = false } = {}) {
  return {
    id: id || newSavedFilterId(),
    name: String(name || '').trim(),
    dataset: draft?.dataset || '',
    intervalMode: draft?.intervalMode === 'lith1_code' ? 'lith1_code' : 'assay',
    combinator: draft?.combinator === 'OR' ? 'OR' : 'AND',
    isPlan: !!draft?.isPlan,
    rules: cloneFilterRules(draft?.rules),
    shared: !!shared,
    builtin: false,
  };
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function presetDisplayName(model) {
  const ds = String(model?.dataset || '').trim();
  const raw = String(model?.displayName || model?.label || model?.name || '').trim();
  let rest = raw;
  if (ds) {
    const re = new RegExp(`(^|_)${escapeRegExp(ds)}(_|$)`, 'ig');
    rest = raw
      .replace(re, '$1')
      .replace(/__+/g, '_')
      .replace(/^_+|_+$/g, '');
  }
  rest = rest.replace(/_LITH$/i, '').replace(/^_+|_+$/g, '');
  if (ds && rest && rest.toLowerCase() !== ds.toLowerCase()) return `${ds} · ${rest}`;
  if (ds && model?.holetype) return `${ds} · ${model.holetype}`;
  return rest || ds || 'Holes';
}

function layerToSavedFilter(model) {
  const rules = [];
  if (model?.holetype) {
    rules.push({
      id: newRuleId(),
      field: 'Hole_Type',
      op: 'eq',
      value: String(model.holetype),
      valueTo: '',
    });
  }
  const lith = model?.interval_mode === 'lith1_code' || /_LITH$/i.test(String(model?.name || ''));
  return {
    id: `builtin_${String(model?.dataset || 'ds')}_${String(model?.holetype || 'all')}`,
    name: presetDisplayName(model),
    dataset: model?.dataset || '',
    intervalMode: lith ? 'lith1_code' : 'assay',
    combinator: 'AND',
    isPlan: false,
    rules,
    builtin: true,
  };
}

/** Catalogue databaseHoles rows → saved-filter prefills (assay/lith pairs collapsed). */
export function extractDatabaseHolePresets(modelList) {
  const layers = [];
  (modelList || []).forEach((section) => {
    (section.models || []).forEach((model) => {
      if (String(model?.type || '').toLowerCase() !== 'databaseholes') return;
      layers.push(model);
    });
  });

  const byKey = new Map();
  layers.forEach((model) => {
    const key = `${model.dataset || ''}\0${model.holetype || ''}`;
    const lith = model.interval_mode === 'lith1_code' || /_LITH$/i.test(String(model.name || ''));
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, layerToSavedFilter(model));
      return;
    }
    if (!lith) byKey.set(key, layerToSavedFilter({ ...model, interval_mode: 'assay' }));
  });

  return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function stripDatabaseHoles(modelList) {
  return (modelList || [])
    .map((section) => ({
      ...section,
      models: (section.models || []).filter((model) => String(model?.type || '').toLowerCase() !== 'databaseholes'),
    }))
    .filter((section) => (section.models || []).length > 0);
}
